/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { GridApi, IRowNode } from '@ag-grid-community/core';
import { computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Subject, switchMap } from 'rxjs';
import { debounceTime, filter, map, startWith, tap } from 'rxjs/operators';

import { injectListOrganizationsWithTotalsQuery } from '@models/organization/list-organizations-with-totals.query';

import {
  ActivitySubType,
  ActivityType,
  BudgetActivityAttributes,
  BudgetData,
  BudgetExpenseData,
  BudgetType,
  CategoryType,
  ExpenseType,
  GqlService,
  listCategorySettingsQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { ArrElement, Maybe } from '@shared/utils/utils';

import { BeActivitiesAttributesModalComponent } from './be-activities-attributes-modal/be-activities-attributes-modal.component';
import {
  BeActivitiesAttributesModalParams,
  BeActivitiesAttributesModalReturnData,
  BeActivitiesAttributesModalRowData,
  BeActivityTypes,
} from './be-activities-attributes-modal/be-activities-attributes-modal.model';
import { BeInlineCategoryDropdownOption } from './be-inline-category-dropdown/be-inline-category-dropdown.model';
import { getAttributeColumns } from '../budget-attributes/services/budget-attributes.service';

export const injectOpenActivitiesModal = (
  gqlService = inject(GqlService),
  overlayService = inject(OverlayService),
  organizationQuery = injectListOrganizationsWithTotalsQuery()
) => {
  const vendor_id = signal('');
  const vendor = computed(() => (organizationQuery.data() || []).find((o) => o.id === vendor_id()));
  const change_order_id = signal<string | undefined>(undefined);

  const categoriesRefresh$ = new Subject();
  const categoriesLoading = signal(false);
  const categories = toSignal(
    combineLatest([
      toObservable(vendor_id).pipe(filter(Boolean)),
      toObservable(change_order_id),
      categoriesRefresh$.pipe(startWith(null)),
    ]).pipe(
      debounceTime(100),
      switchMap(([vendor_id, change_order_id]) => {
        categoriesLoading.set(true);

        return gqlService
          .listCategorySettings$({
            budget_type: change_order_id
              ? BudgetType.BUDGET_CHANGE_ORDER
              : BudgetType.BUDGET_PRIMARY,
            change_order_id,
            vendor_id,
          })
          .pipe(
            map(mapCategories),
            tap(() => {
              categoriesLoading.set(false);
            })
          );
      })
    ),
    { initialValue: [] }
  );

  const mapSelectedRowsToModalData = (
    gridAPI: GridApi,
    rowOverrideFn?: (node: IRowNode) => Partial<BeActivitiesAttributesModalRowData>
  ) => {
    const categoriesArr = categories();

    return gridAPI.getSelectedNodes().map((node) => {
      const x = node.data;
      let cat: Maybe<ArrElement<typeof categoriesArr>>;
      const root_id = categoriesArr?.[0].id;

      if (x.category_id && x.category_id !== root_id) {
        cat = categoriesArr?.filter((c) => c.id === x.category_id)[0];
      }

      if (!cat) {
        cat = categoriesArr?.find(
          (c) =>
            c.name === (x.group4 || x.group3 || x.group2 || x.group1 || x.group0 || x.cost_category)
        );
      }

      if (x.cost_category === 'Investigator') {
        cat = categoriesArr?.find(
          (c) =>
            c.name === x.activity_name_label &&
            (
              [
                ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS,
                ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES,
                ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES,
              ] as BeActivityTypes[]
            ).includes(c.type)
        );
      }

      const isPercentage = cat?.type === ActivityType.ACTIVITY_DISCOUNT && x.contract_unit_cost > 0;
      const actualsToDateExists = !(x.expenses as BudgetExpenseData[])
        .filter((c) => c.expense_type === ExpenseType.EXPENSE_WP)
        .every((z) => z.amount === 0);

      return <BeActivitiesAttributesModalRowData>{
        activity_name: x.activity_name_label,
        activity_type: cat?.type || '',
        actualsToDateExists,
        attributes: ((x.attributes || []) as BudgetActivityAttributes[]).reduce(
          (acc, val) => {
            if (val.attribute_name) {
              acc[getEncodedAttributeName(val.attribute_name)] = val.attribute_value || '';
            }

            return acc;
          },
          {} as Record<string, string>
        ),
        category: cat?.id || x.category_id,
        changed: false,
        currencyName: x.contract_unit_cost_currency,
        deletable: !actualsToDateExists,
        deleted: false,
        display_label: x.display_label,
        id: x.activity_id,
        isGenerated: false,
        isPercentage,
        unit_cost: x.contract_unit_cost || 0,
        unit_num: x.unit_num || 0,
        uom: isPercentage ? 'Percentage' : x.uom,
        vendor_id: x.vendor_id,
        ...(rowOverrideFn?.(node) || {}),
      };
    });
  };

  const openModal = ({
    allBudgetActivities,
    budgetData,
    editMode = true,
    isActivityTabVisible = true,
    isAttributesEditable = true,
    notEditableAttributesTooltip = '',
    rows,
    showNotesSection = true,
    uomHide = false,
  }: {
    allBudgetActivities: BudgetData[];
    budgetData: BudgetData[];
    editMode?: boolean;
    isActivityTabVisible?: boolean;
    isAttributesEditable?: boolean;
    notEditableAttributesTooltip?: string;
    rows: BeActivitiesAttributesModalRowData[];
    showNotesSection?: boolean;
    uomHide?: boolean;
  }) => {
    const v = vendor();

    if (!v) {
      return;
    }

    const categoriesArr = categories();

    if (!categoriesArr?.length) {
      getDefaultCategories('0', false).forEach((c) => {
        categoriesArr?.push(c);
      });
    }

    const usedCategories = {
      [ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES]: false,
      [ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS]: false,
      [ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES]: false,
      [ActivityType.ACTIVITY_DISCOUNT]: false,
    } as Record<BeActivityTypes, boolean>;

    budgetData
      .filter((x) => x.vendor_id === v.id)
      .forEach((x) => {
        if (x.cost_category === 'Investigator') {
          Object.keys(ActivitySubType).forEach((c) => {
            if (x.activity_sub_type === c) {
              usedCategories[c] = true;
            }
          });
        } else if (x.cost_category === 'Discount') {
          usedCategories[ActivityType.ACTIVITY_DISCOUNT] = true;
        }
      });

    const attributes = getAttributeColumns({
      attributes: allBudgetActivities.map((z) =>
        (z.attributes || [])
          .filter((z) => z.attribute_name)
          .map((z) => ({ ...z, attribute_value: z.attribute_value || '' }))
      ),
    }) as {
      attribute_name: string;
      attribute_value: string;
    }[];

    return overlayService.openPopup<
      BeActivitiesAttributesModalParams,
      BeActivitiesAttributesModalReturnData,
      BeActivitiesAttributesModalComponent
    >({
      content: BeActivitiesAttributesModalComponent,
      data: {
        attributes,
        categories: categoriesArr || [],
        containerClass: ['sm:!pb-4', 'sm:!px-4'],
        currencyName: v.currency || '',
        editMode,
        isActivityTabVisible,
        isAttributesEditable,
        notEditableAttributesTooltip,
        rows,
        showNotesSection,
        uomHide,
        usedCategories,
        vendor: {
          id: v.id,
          label: v.name || '',
        },
      },
      settings: {
        header: (instance) =>
          instance?.isActivitiesTabVisible ? 'Manage Activities' : 'Edit Attributes',
        primaryButton: {
          action: (instance) =>
            instance?.isActivitiesTabVisible
              ? instance?.activitiesTabComponent?.goToAttributesTab()
              : instance?.attributesTabComponent?.onSave(),
          disabled: (instance) =>
            instance?.loading() || instance?.isActivitiesTabVisible
              ? !!instance?.activitiesTabComponent?.editButtonDisabled()
              : !!instance?.attributesTabComponent?.isSaveBtnDisabled(),
          label: (instance) =>
            instance?.isActivitiesTabVisible ? 'Edit Attributes' : 'Save & Exit',
          loading: (instance) => !!instance?.loading(),
          tooltip: (instance) =>
            (instance?.isActivitiesTabVisible
              ? (instance?.activitiesTabComponent?.editButtonDisabled() &&
                  instance?.activitiesTabComponent?.editButtonTooltip) ||
                ''
              : instance?.attributesTabComponent?.saveBtnTooltip()) || '',
        },
        secondaryButton: {
          action: (instance) =>
            instance?.isActivitiesTabVisible
              ? instance?.activitiesTabComponent?.onSaveActivities()
              : instance?.attributesTabComponent?.goToActivitiesTab(),
          disabled: (instance) => {
            if (!instance) {
              return true;
            }

            if (instance.loading()) {
              return true;
            }

            if (instance.isActivitiesTabVisible) {
              return instance.activitiesTabComponent?.isSaveButtonDisabledAct() || false;
            }

            if (instance.attributesTabComponent?.doesHeaderHasError()) {
              return true;
            }

            if (instance.attributesTabComponent?.ifAnyRowHasError()) {
              return true;
            }

            return false;
          },
          label: (instance) =>
            instance?.isActivitiesTabVisible ? 'Save & Exit' : 'Edit Activities',
          loading: (instance) => !!instance?.loading(),
          tooltip: (instance) => {
            if (!instance) {
              return '';
            }

            if (instance.isActivitiesTabVisible) {
              return instance.activitiesTabComponent?.saveBtnTooltipAct() || '';
            }

            if (instance.attributesTabComponent?.doesHeaderHasError()) {
              return instance.attributesTabComponent?.headerErrorTooltip || '';
            }

            if (instance.attributesTabComponent?.ifAnyRowHasError()) {
              return instance.attributesTabComponent?.rowErrorTooltip || '';
            }

            return '';
          },
        },
        tertiaryButton: {},
      },
    });
  };

  return {
    categories,
    categoriesLoading,
    change_order_id,
    mapSelectedRowsToModalData,
    openModal,
    refreshCategories: () => categoriesRefresh$.next(null),
    vendor_id,
  };
};

const mapCategories = ({ data }: GraphqlResponse<listCategorySettingsQuery>) => {
  if (!data) {
    return [];
  }

  const categories: BeInlineCategoryDropdownOption[] = [];
  const rootID = data.id;

  type A = {
    categories?: A[];
    category_type: CategoryType;
    fullPath?: string;
    id: string;
    name: string;
    path?: string;
  };

  const addToCategoriesRecursive = (sc: A, type: BeActivityTypes, indent: number, path: string) => {
    const result = categoryToActivityType(sc.category_type);

    if (result === type) {
      const categoryPath = sc.id === rootID ? '' : path || '';
      const categoryFullPath = categoryPath + '>' + sc.name;

      categories.push({
        categoryType: sc.category_type,
        disabled: false,
        fullPath: categoryFullPath,
        id: sc.id,
        indent,
        name: sc.name,
        path: categoryPath,
        type: result,
      });

      if (sc.categories?.length) {
        sc.categories.forEach((ssc) => {
          addToCategoriesRecursive(ssc, type, indent + 1, categoryFullPath);
        });
      }
    }
  };

  getDefaultCategories(rootID, false).forEach((c) => {
    categories.push(c);

    if (!c.type.startsWith('ACTIVITY_INVESTIGATOR_')) {
      data.categories.forEach((sc) => {
        addToCategoriesRecursive(sc, c.type, 2, '');
      });
    }
  });

  return categories;
};

const categoryToActivityType = (c: CategoryType) => {
  switch (c) {
    case CategoryType.CATEGORY_DISCOUNT:
      return ActivityType.ACTIVITY_DISCOUNT;
    case CategoryType.CATEGORY_INVESTIGATOR:
      return ActivityType.ACTIVITY_INVESTIGATOR;
    case CategoryType.CATEGORY_PASSTHROUGH:
      return ActivityType.ACTIVITY_PASSTHROUGH;
    case CategoryType.CATEGORY_ROOT:
    case CategoryType.CATEGORY_SERVICE:
    case CategoryType.CATEGORY_UNDETERMINED:
      return ActivityType.ACTIVITY_SERVICE;
  }
};

const getDefaultCategories = (rootID: string, disabled: boolean) => {
  return [
    {
      categoryType: CategoryType.CATEGORY_SERVICE,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 1,
      name: 'Services',
      path: '',
      type: ActivityType.ACTIVITY_SERVICE,
    },
    {
      categoryType: CategoryType.CATEGORY_DISCOUNT,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 1,
      name: 'Discount',
      path: '',
      type: ActivityType.ACTIVITY_DISCOUNT,
    },
    {
      categoryType: CategoryType.CATEGORY_PASSTHROUGH,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 1,
      name: 'Pass-through',
      path: '',
      type: ActivityType.ACTIVITY_PASSTHROUGH,
    },
    {
      categoryType: CategoryType.CATEGORY_INVESTIGATOR,
      disabled: true,
      fullPath: '',
      id: rootID,
      indent: 1,
      name: 'Investigator',
      path: '',
      type: ActivityType.ACTIVITY_INVESTIGATOR,
    },
    {
      categoryType: CategoryType.CATEGORY_INVESTIGATOR,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 2,
      name: 'Patient Visits',
      path: '',
      type: ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_VISITS,
    },
    {
      categoryType: CategoryType.CATEGORY_INVESTIGATOR,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 2,
      name: 'Patient Invoiceables',
      path: '',
      type: ActivitySubType.ACTIVITY_INVESTIGATOR_PATIENT_INVOICEABLES,
    },
    {
      categoryType: CategoryType.CATEGORY_INVESTIGATOR,
      disabled,
      fullPath: '',
      id: rootID,
      indent: 2,
      name: 'Site Invoiceables',
      path: '',
      type: ActivitySubType.ACTIVITY_INVESTIGATOR_SITE_INVOICEABLES,
    },
  ];
};
